/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
      this.initClearCartButton();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.cartPage = this.closest('.cart-page-modern');
      this.itemStatus = document.getElementById('cart-line-item-status');

      // Support both old (.cart-item) and new (.cart-item-modern) structures
      const quantityInputs = this.querySelectorAll('[name="updates[]"]');
      this.currentTotalItemCount = Array.from(quantityInputs).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0
      );

      // If no quantity inputs (modern design), calculate from qty values
      if (quantityInputs.length === 0) {
        this.currentTotalItemCount = Array.from(this.querySelectorAll('.js-qty-value')).reduce(
          (total, qtyEl) => total + parseInt(qtyEl.textContent, 10),
          0
        );
      }

      this.currentQuantities = [];
      // Support both old and new cart item selectors
      this.querySelectorAll('.cart-item, .cart-item-modern').forEach((item) => {
        const qtyInput = item.querySelector('.qty-input__input');
        const qtyValue = item.querySelector('.js-qty-value');
        if (qtyInput) {
          this.currentQuantities[item.dataset.variantId] = Number(qtyInput.value);
        } else if (qtyValue) {
          this.currentQuantities[item.dataset.variantId] = Number(qtyValue.textContent);
        }
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));
    }

    /**
     * Initializes the clear cart button on the cart page
     */
    initClearCartButton() {
      // Only for cart page (not drawer - drawer has its own handler)
      if (this.cartDrawer) return;

      const cartPage = this.closest('.cart-page-modern');
      if (!cartPage) return;

      // Use event delegation so listener survives header re-renders
      cartPage.addEventListener('click', async (evt) => {
        const clearBtn = evt.target.closest('.js-clear-cart');
        if (!clearBtn) return;
        evt.preventDefault();
        await this.clearCart(clearBtn);
      });
    }

    /**
     * Clears all items from the cart
     * @param {Element} button - The button element that triggered the clear
     */
    async clearCart(button) {
      try {
        if (button) {
          button.disabled = true;
          button.classList.add('is-loading');
        }

        const response = await fetch(`${theme.routes.cart}/clear.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          }
        });

        if (!response.ok) throw new Error('Failed to clear cart');

        // Reload the page to show empty state
        window.location.reload();
      } catch (error) {
        console.error('Error clearing cart:', error);
      } finally {
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      }
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      // Handle remove item click
      const removeBtn = evt.target.closest('.js-remove-item');
      if (removeBtn) {
        evt.preventDefault();
        this.updateQuantity(removeBtn.dataset.index, 0);
        return;
      }

      // Handle modern quantity buttons (+/-)
      const qtyBtn = evt.target.closest('.js-qty-btn');
      if (qtyBtn) {
        evt.preventDefault();

        // Don't process if button is disabled (at stock limit)
        if (qtyBtn.disabled || qtyBtn.classList.contains('is-disabled')) return;

        const index = qtyBtn.dataset.index;
        const action = qtyBtn.dataset.action;
        const qtyValueEl = this.querySelector(`.js-qty-value[data-index="${index}"]`);
        if (!qtyValueEl) return;

        let currentQty = parseInt(qtyValueEl.textContent, 10);
        let newQty = action === 'plus' ? currentQty + 1 : currentQty - 1;
        newQty = Math.max(0, newQty);

        // Check stock limit for plus action
        if (action === 'plus') {
          const inventoryQty = parseInt(qtyValueEl.dataset.inventory, 10);
          const inventoryPolicy = qtyValueEl.dataset.inventoryPolicy;
          if (inventoryPolicy === 'deny' && !isNaN(inventoryQty) && newQty > inventoryQty) {
            return; // Don't allow increasing beyond stock
          }
        }

        this.updateQuantity(index, newQty);
        return;
      }
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.dataset.index, evt.target.value, document.activeElement.name);
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    async updateQuantity(line, quantity, name) {
      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);
      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      const sections = this.getSectionsToRender().map((section) => section.section);
      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      });

      try {
        const lineItem = document.getElementById(`cart-item-${line}`);
        const variantId = Number(lineItem.dataset.variantId);
        const oldTotalQuantity = this.currentTotalItemCount;
        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        const newTotalQuantity = data.item_count;

        // When cart becomes empty in the drawer, do a full refresh since the
        // drawer structure differs completely between empty and non-empty states.
        if (this.cartDrawer && newTotalQuantity === 0) {
          if (oldTotalQuantity !== newTotalQuantity) {
            this.dispatchEvent(new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                cart: data,
                variantId,
                oldQuantity: this.currentQuantities[variantId],
                newQuantity: Number(quantity)
              }
            }));
          }
          this.cartDrawer.refresh(true);
          return;
        }

        if (this.cartDrawer) {
          cartDrawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);
        } else if (newTotalQuantity === 0) {
          // We're on the Cart page
          const mainCart = this.closest('.cc-main-cart');

          // Handle old cart page header
          const cartTitle = mainCart.querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          // Handle new cart page header
          const cartHeader = mainCart.querySelector('.cart-page__header');
          if (cartHeader) {
            const clearBtn = cartHeader.querySelector('.js-clear-cart');
            if (clearBtn) clearBtn.hidden = true;
          }

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartContent = document.getElementById('cart-summary-and-blocks');
          if (cartContent) cartContent.hidden = true;
        }

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const { selector } = section;
          const el = sectionEl.querySelector(selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data.sections[section.section], selector);
        });

        this.updateRecommendations(data.item_count > 0 ? data.items[0].product_id : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: data,
              variantId,
              oldQuantity: this.currentQuantities[variantId],
              newQuantity: Number(quantity)
            }
          }));
        }

        this.currentQuantities[variantId] = Number(quantity);

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        // Hide all loaders (both old and modern)
        this.querySelectorAll('.cart-item__loader, .cart-item-modern__loader').forEach((loader) => {
          loader.hidden = true;
        });

        // Remove loading class from modern items
        this.querySelectorAll('.cart-item-modern--loading').forEach((item) => {
          item.classList.remove('cart-item-modern--loading');
        });

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

        // Reset quantity input (for old design)
        const input = document.getElementById(`quantity-${line}`);
        if (input) {
          input.value = input.dataset.initialValue;
          const quantityInput = input.closest('quantity-input');
          if (quantityInput) quantityInput.currentQty = input.dataset.initialValue;
        }
      } finally {
        this.classList.remove('pointer-events-none');

        // Remove loading class from modern items
        this.querySelectorAll('.cart-item-modern--loading').forEach((item) => {
          item.classList.remove('cart-item-modern--loading');
        });

        // Attempt to maintain the same scroll position in the cart drawer
        if (cartDrawerContent) {
          requestAnimationFrame(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; });
          setTimeout(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; }, 0);
          requestAnimationFrame(() => { this.cartDrawer.scrollTop = cartDrawerScroll; });
          setTimeout(() => { this.cartDrawer.scrollTop = cartDrawerScroll; }, 0);
        }
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    async refresh() {
      const errors = document.getElementById('cart-errors');
      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${[...new Set(sections)]}`);

        // The status is 400, refresh the whole cart and stop
        if (response.status === 400 && this.cartDrawer) {
          this.cartDrawer.refresh(true);
          return;
        }

        const data = await response.json();

        if (!response.ok) throw new Error(response.status);

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data[section.section], section.selector);
        });

        const firstCartItem = this.querySelector('.cart-item:first-child');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        errors.innerHTML = '';
        errors.hidden = true;
      } catch (error) {
        errors.textContent = theme.strings.cartError;
        errors.hidden = false;
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__header-modern'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary, .cart-drawer__footer-modern'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          }
        ];
      } else {
        // Support both old (.cart__summary) and new (.cart-page__summary) selectors
        const cartSummary = document.getElementById('cart-summary');
        const summarySelector = cartSummary.classList.contains('cart-page__summary')
          ? '.cart-page__summary'
          : '.cart__summary';

        sections = [
          ...sections,
          {
            id: 'cart-page-header',
            section: this.dataset.section,
            selector: '.cart-page__header'
          },
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          },
          {
            id: 'cart-summary',
            section: cartSummary.dataset.section,
            selector: summarySelector
          }
        ];
      }

      return sections;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.classList.add('pointer-events-none');

      // Support both old (.cart-item__loader) and new (.cart-item-modern__loader) loaders
      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader, #cart-item-${line} .cart-item-modern__loader`);
      if (loader) loader.hidden = false;

      // Add loading class to the cart item (for modern design)
      const cartItem = this.querySelector(`#cart-item-${line}`);
      if (cartItem) cartItem.classList.add('cart-item-modern--loading');

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          // Support both old and new empty cart selectors
          const emptyContainer = this.cartDrawer.querySelector('.js-cart-empty, .cart-drawer__empty');
          trapFocus(
            emptyContainer,
            this.cartDrawer.querySelector('a')
          );
        } else if (this.cartDrawer.querySelector('.cart-item, .cart-item-modern')) {
          // Support both old and new item title selectors
          const itemTitle = document.querySelector('.js-item-name, .cart-item-modern__title');
          trapFocus(this.cartDrawer, itemTitle);
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}
